import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import './ManageBhaktiBalChitraKatha.css';

const categoryOptions = [
  { id: 'krishna-leela', name: 'Krishna Leela' },
  { id: 'ramayana', name: 'Ramayana Stories' },
  { id: 'moral-stories', name: 'Moral Stories' },
  { id: 'bhajans', name: 'Kids Bhajans' }
];

const createEmptyVideo = () => ({
  title: '',
  youtubeVideoId: '',
  duration: '',
  ageGroup: ''
});

const initialForm = {
  channelId: '',
  name: '',
  categoryId: categoryOptions[0].id,
  description: '',
  youtubeChannelUrl: '',
  thumbnail: '',
  videos: [createEmptyVideo()]
};

const ManageBhaktiBalChitraKatha = () => {
  const [channels, setChannels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingChannelId, setEditingChannelId] = useState('');
  const [formData, setFormData] = useState(initialForm);

  const fetchChannels = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiService.getAdminBalChitraKathaChannels();
      setChannels(response?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load channels.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChannels();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVideoChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      videos: prev.videos.map((video, videoIndex) => (
        videoIndex === index ? { ...video, [field]: value } : video
      ))
    }));
  };

  const addVideoRow = () => {
    setFormData((prev) => ({
      ...prev,
      videos: [...prev.videos, createEmptyVideo()]
    }));
  };

  const removeVideoRow = (index) => {
    setFormData((prev) => {
      if (prev.videos.length === 1) return prev;
      return {
        ...prev,
        videos: prev.videos.filter((_, videoIndex) => videoIndex !== index)
      };
    });
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingChannelId('');
  };

  const startEdit = (channel) => {
    setError('');
    setEditingChannelId(channel.channelId);
    setFormData({
      channelId: channel.channelId || '',
      name: channel.name || '',
      categoryId: channel.categoryId || categoryOptions[0].id,
      description: channel.description || '',
      youtubeChannelUrl: channel.youtubeChannelUrl || '',
      thumbnail: channel.thumbnail || '',
      videos: Array.isArray(channel.videos) && channel.videos.length > 0
        ? channel.videos.map((video) => ({
          id: video.id || '',
          title: video.title || '',
          youtubeVideoId: video.youtubeVideoId || '',
          duration: video.duration || '',
          ageGroup: video.ageGroup || ''
        }))
        : [createEmptyVideo()]
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (editingChannelId) {
        await apiService.updateAdminBalChitraKathaChannel(editingChannelId, formData);
      } else {
        await apiService.createAdminBalChitraKathaChannel(formData);
      }
      resetForm();
      await fetchChannels();
    } catch (err) {
      setError(err.message || `Failed to ${editingChannelId ? 'update' : 'create'} channel.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (channelId) => {
    if (!window.confirm('Delete this channel and all its videos?')) return;

    setError('');
    try {
      await apiService.deleteAdminBalChitraKathaChannel(channelId);
      await fetchChannels();
    } catch (err) {
      setError(err.message || 'Failed to delete channel.');
    }
  };

  return (
    <div className="manage-bal-katha-container">
      <div className="header-section">
        <h2>Manage Bhakti Bal Chitra Katha</h2>
      </div>

      {error ? <div className="error-message">Error: {error}</div> : null}

      <form className="bal-katha-form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="name">Channel Name (Display Title)</label>
            <input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g. Jaguni (optional, auto-fetched from YouTube)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="channelId">Channel ID (optional)</label>
            <input
              id="channelId"
              name="channelId"
              value={formData.channelId}
              onChange={handleInputChange}
              placeholder="auto-generated if empty"
              disabled={Boolean(editingChannelId)}
            />
          </div>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="categoryId">Category</label>
            <select
              id="categoryId"
              name="categoryId"
              value={formData.categoryId}
              onChange={handleInputChange}
            >
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="youtubeChannelUrl">YouTube Channel URL</label>
            <input
              id="youtubeChannelUrl"
              name="youtubeChannelUrl"
              value={formData.youtubeChannelUrl}
              onChange={handleInputChange}
              placeholder="https://www.youtube.com/@JaguniJagannath"
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="thumbnail">Thumbnail URL</label>
          <input
            id="thumbnail"
            name="thumbnail"
            value={formData.thumbnail}
            onChange={handleInputChange}
            placeholder="Optional, auto-fetched from channel"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Optional, auto-fetched from channel"
            rows={3}
          />
        </div>

        <div className="video-block-header">
          <h3>Channel Videos</h3>
          <button type="button" className="action-btn" onClick={addVideoRow}>
            <i className="fas fa-plus"></i> Add Video
          </button>
        </div>
        <p className="video-help-text">
          Tip: On save, the system auto-imports playlist videos from the channel URL.
        </p>

        {formData.videos.map((video, index) => (
          <div className="video-row" key={`${index}-${video.youtubeVideoId}`}>
            <div className="form-grid">
              <div className="form-group">
                <label>Video Title</label>
                <input
                  value={video.title}
                  onChange={(event) => handleVideoChange(index, 'title', event.target.value)}
                  placeholder="e.g. Little Krishna Stories (optional if URL added)"
                />
              </div>
              <div className="form-group">
                <label>YouTube Video URL or ID</label>
                <input
                  value={video.youtubeVideoId}
                  onChange={(event) => handleVideoChange(index, 'youtubeVideoId', event.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... or 11-char ID"
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Duration (optional)</label>
                <input
                  value={video.duration}
                  onChange={(event) => handleVideoChange(index, 'duration', event.target.value)}
                  placeholder="e.g. 10:23"
                />
              </div>
              <div className="form-group">
                <label>Age Group (optional)</label>
                <input
                  value={video.ageGroup}
                  onChange={(event) => handleVideoChange(index, 'ageGroup', event.target.value)}
                  placeholder="e.g. 5-12"
                />
              </div>
            </div>

            <div className="video-row-actions">
              <button
                type="button"
                className="action-btn btn-delete"
                onClick={() => removeVideoRow(index)}
                disabled={formData.videos.length === 1}
              >
                <i className="fas fa-trash"></i> Remove Video
              </button>
            </div>
          </div>
        ))}

        <div className="form-action-buttons">
          <button type="submit" className="btn-primary-admin" disabled={isSaving}>
            {isSaving ? 'Saving...' : editingChannelId ? 'Update Channel' : 'Add Channel'}
          </button>
          {editingChannelId ? (
            <button type="button" className="action-btn" onClick={resetForm} disabled={isSaving}>
              Cancel Edit
            </button>
          ) : null}
        </div>
      </form>

      <div className="entries-table-wrapper">
        <table className="entries-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Category</th>
              <th>Videos</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="4" className="no-results">Loading channels...</td>
              </tr>
            ) : channels.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-results">No admin-added channels yet.</td>
              </tr>
            ) : (
              channels.map((channel) => (
                <tr key={channel._id}>
                  <td data-label="Channel">
                    <strong>{channel.name}</strong>
                    <p className="entry-content-preview">{channel.description}</p>
                    <a href={channel.youtubeChannelUrl} target="_blank" rel="noopener noreferrer">
                      Open channel
                    </a>
                  </td>
                  <td data-label="Category">{channel.categoryId}</td>
                  <td data-label="Videos">
                    <span className="status-badge active">{channel.videos?.length || 0} videos</span>
                  </td>
                  <td data-label="Actions" className="action-cell">
                    <button className="action-btn" onClick={() => startEdit(channel)}>
                      <i className="fas fa-pen"></i> Edit
                    </button>
                    <button className="action-btn btn-delete" onClick={() => handleDelete(channel.channelId)}>
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageBhaktiBalChitraKatha;
